import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { Button } from 'antd';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { PageProps } from '@/types/PageProps';
import {
  AppPageHeader,
  ColumnsType,
  InfiniteTable,
  TabContentWrapper,
} from '@/components/_common';
import { useMaterialList } from '@/core/domain/stock/Material/Material.hooks';
import {
  Material,
  MaterialListRequest,
  MaterialType,
  materialTypeColor,
  materialTypeLabel,
} from '@/core/domain/stock/Material/Material.types';
import StatusTag from '@/components/_common/StatusTag';
import { useState } from 'react';
import { useDebounce } from '@/hooks';
import EyeIcon from '@/components/_common/EyeIcon';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import { hasPermissions } from '@/utils/functions/hasPermissions';

function DeliveryOfMaterialsPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState<MaterialListRequest>({
    order: 'DESC',
    rows: 10,
    ...convertParams<MaterialListRequest>({
      filter: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useMaterialList(queryDebounce);

  const materials = flatInfiniteResult(data);

  const columns: ColumnsType<Material> = [
    {
      title: intl.formatMessage({
        id: 'deliveryOrReturnOfMaterials.columns.table.company',
      }),
      dataIndex: 'company',
      ellipsis: true,
      width: 240,
    },
    {
      dataIndex: 'deliveredOrReturnedMan',
      title: intl.formatMessage({
        id: 'deliveryOrReturnOfMaterials.columns.table.deliveredOrReturnedBy',
      }),
      width: 250,
      ellipsis: true,
    },
    {
      dataIndex: 'received',
      title: intl.formatMessage({
        id: 'deliveryOrReturnOfMaterials.columns.table.receivedBy',
      }),
      ellipsis: true,
      width: 250,
    },
    {
      title: intl.formatMessage({
        id: `deliveryOrReturnOfMaterials.columns.table.date`,
      }),
      dataIndex: 'date',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 136,
    },
    {
      dataIndex: 'type',
      title: intl.formatMessage({
        id: 'deliveryOrReturnOfMaterials.columns.table.deliveryOrReturn',
      }),
      width: 200,
      render: (_, row) => (
        <StatusTag
          color={materialTypeColor[row.type]}
          label={materialTypeLabel[row.type]}
        />
      ),
    },
    {
      key: 'actions',
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      width: 50,
      fixed: 'right',
      align: 'right',
      render: (_, row) =>
        hasPermissions([Authorities.MOVIMENTATION_MATERIAL_VIEW]) && (
          <EyeIcon
            redirect={`/app/movements/deliveryOfMaterials/${row.id}/${
              row?.type === MaterialType.Delivered ? 'delivery' : 'return'
            }`}
          />
        ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <SpaceForButton>
            <Button
              onClick={() =>
                history.push(
                  '/app/movements/deliveryOfMaterials/returnMaterial',
                )
              }
              type="primary"
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_MATERIAL_RETURN_MATERIAL,
                ])
              }
              block
            >
              {intl.formatMessage({
                id: 'deliveryOrReturnOfMaterials.create.returnMaterial',
              })}
            </Button>

            <Button
              type="primary"
              onClick={() =>
                history.push(
                  '/app/movements/deliveryOfMaterials/deliverMaterial',
                )
              }
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_MATERIAL_DELIVER_MATERIAL,
                ])
              }
              block
            >
              {intl.formatMessage({
                id: 'deliveryOrReturnOfMaterials.create.deliverMaterial',
              })}
            </Button>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, filter: value }))
          }
          searchValue={queryParams.filter}
        />

        <InfiniteTable<Material>
          loading={isLoading}
          rowKey={(row) => row.id}
          dataSource={materials}
          columns={columns}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetching={isFetching}
        />
      </TabContentWrapper>
    </>
  );
}

export default DeliveryOfMaterialsPage;
